import React, { useMemo, useState } from 'react';
import { MoveBreakdown, getMediaUrl } from '../services/api';
import './BreakdownRows.css';

// Grouped swipe rows for move breakdowns: one horizontally-scrolling row per
// dance role, clips in instructor-curated `order`. "Both partners" clips are
// appended to each role's row (a lead learning their part still wants the
// partnered view). Clip type is the card title; angle/counts are metadata —
// deliberately no clip-type sub-filter: at 3-10 clips a well-ordered row
// beats hiding cards behind controls.

interface BreakdownRowsProps {
  breakdowns: MoveBreakdown[];
  onPlay: (breakdown: MoveBreakdown) => void;
}

const ROLE_ORDER = ['Lead', 'Follow'];
const byOrder = (a: MoveBreakdown, b: MoveBreakdown) => a.order - b.order;

const BreakdownRows: React.FC<BreakdownRowsProps> = ({ breakdowns, onPlay }) => {
  const [roleFilter, setRoleFilter] = useState('');

  const rows = useMemo(() => {
    const both = breakdowns.filter((b) => b.role.name === 'Both').sort(byOrder);
    const result: { role: string; clips: MoveBreakdown[] }[] = [];
    for (const role of ROLE_ORDER) {
      const own = breakdowns.filter((b) => b.role.name === role).sort(byOrder);
      if (own.length > 0) result.push({ role, clips: [...own, ...both] });
    }
    if (result.length === 0 && both.length > 0) {
      result.push({ role: 'Together', clips: both });
    }
    return result;
  }, [breakdowns]);

  const visibleRows = roleFilter ? rows.filter((r) => r.role === roleFilter) : rows;

  return (
    <div className="breakdown-rows">
      {rows.length > 1 && (
        <div className="breakdown-role-chips" role="tablist" aria-label="Filter by role">
          <button
            className={`btn btn-small ${roleFilter === '' ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => setRoleFilter('')}
          >
            All
          </button>
          {rows.map((r) => (
            <button
              key={r.role}
              className={`btn btn-small ${roleFilter === r.role ? 'btn-primary' : 'btn-secondary'}`}
              onClick={() => setRoleFilter(roleFilter === r.role ? '' : r.role)}
            >
              {r.role}
            </button>
          ))}
        </div>
      )}

      {visibleRows.map((row) => (
        <section key={row.role} className="breakdown-role-section">
          <div className="breakdown-row-header">
            <h5>{row.role}</h5>
            <span className="breakdown-row-count">
              {row.clips.length} clip{row.clips.length !== 1 ? 's' : ''}
            </span>
          </div>
          <div className="breakdown-row" aria-label={`${row.role} breakdown clips`}>
            {row.clips.map((bd) => (
              <button
                key={`${row.role}-${bd.id}`}
                type="button"
                className="breakdown-clip"
                onClick={() => onPlay(bd)}
              >
                <div className="breakdown-clip-thumb">
                  {bd.thumbnail ? (
                    <img src={getMediaUrl(bd.thumbnail)} alt="" loading="lazy" />
                  ) : null}
                  <span className="breakdown-clip-play" aria-hidden="true">
                    ▶
                  </span>
                </div>
                <div className="breakdown-clip-info">
                  <div className="breakdown-clip-title">{bd.clipType.name}</div>
                  <div className="breakdown-clip-meta">
                    <span className="breakdown-chip">{bd.angle.name}</span>
                    {bd.counts && <span className="breakdown-chip breakdown-chip-counts">{bd.counts}</span>}
                    {row.role !== 'Together' && bd.role.name === 'Both' && (
                      <span className="breakdown-chip breakdown-chip-partner">with partner</span>
                    )}
                  </div>
                </div>
              </button>
            ))}
          </div>
        </section>
      ))}
    </div>
  );
};

export default BreakdownRows;
